import { useState, useEffect } from 'react';
import {
    TextField,
    FormControlLabel,
    Checkbox,
    Typography,
    Box,
    Card,
    CardContent,
    Stack,
    Chip,
    Grid,
    useMediaQuery,
    useTheme,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
} from '@mui/material';
import {
    Schedule as ScheduleIcon,
    CheckCircle as CheckCircleIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Notifications as NotificationsIcon,
    People as PeopleIcon,
} from '@mui/icons-material';
import {
    useUpdateUserInfoMutation,
    useGetLastSeenQuery,
    useGetUserInfoQuery,
    useGetResourcesProxiesQuery,
} from '@/store/apiSlice';
import { themeConstants, styleHelpers } from '@/theme';
import { formatDate } from '@/utils/helpers';
import BaseModal from '@/components/modals/BaseModal';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { close } from '@/store/uiSlice.ts';
import { useI18nContext } from '@/i18n/i18n-react';

const UserModal = () => {
    const { data: userInfo, refetch: refetchUserInfo } = useGetUserInfoQuery();
    const { LL, setLocale } = useI18nContext();

    const dispatch = useAppDispatch();
    const open = useAppSelector((state) => state.ui.open.user);
    const onClose = () => dispatch(close('user'));

    const [email, setEmail] = useState(userInfo?.mail || '');
    const [language, setLanguage] = useState(userInfo?.language || 'en');
    const [preselect, setPreselect] = useState(userInfo?.preselect || false);
    const [autoExpandEvents, setAutoExpandEvents] = useState(userInfo?.autoExpandEvents || false);
    const [reminders, setReminders] = useState({
        reminder1d: userInfo?.reminder1d || false,
        reminder2d: userInfo?.reminder2d || false,
        reminder3d: userInfo?.reminder3d || false,
        reminder7d: userInfo?.reminder7d || false,
    });
    const [reminderCategory, setReminderCategory] = useState(userInfo?.reminderCategory ?? true);
    const [notifyWhenAbsent, setNotifyWhenAbsent] = useState(userInfo?.notifyWhenAbsent ?? true);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { data: lastSeen = [], isLoading: isLastSeenLoading } = useGetLastSeenQuery(undefined, {
        skip: !open,
    });
    const { data: proxiesData = [] } = useGetResourcesProxiesQuery(
        userInfo ? userInfo.userId : undefined,
        { skip: !open || !userInfo }
    );
    // proxiesData: [{ proxyId, proxyName, userId, userName }]
    // userId = person being represented, proxyId = person acting as proxy
    const myProxies = proxiesData.filter((p) => p.userId === userInfo?.userId); // others who act FOR me
    const iAmProxyFor = proxiesData.filter((p) => p.proxyId === userInfo?.userId); // people I act for
    const showProxyCard = myProxies.length > 0 || iAmProxyFor.length > 0;
    const [updateUserInfo, { isLoading: isUpdating }] = useUpdateUserInfoMutation();

    // Don't render if userInfo is not available
    if (!userInfo) return null;

    useEffect(() => {
        setEmail(userInfo?.mail || '');
        setLanguage(userInfo?.language || 'en');
        setPreselect(userInfo?.preselect || false);
        setAutoExpandEvents(userInfo?.autoExpandEvents || false);
        setReminders({
            reminder1d: userInfo?.reminder1d || false,
            reminder2d: userInfo?.reminder2d || false,
            reminder3d: userInfo?.reminder3d || false,
            reminder7d: userInfo?.reminder7d || false,
        });
        setReminderCategory(userInfo?.reminderCategory ?? true);
        setNotifyWhenAbsent(userInfo?.notifyWhenAbsent ?? true);
    }, [userInfo]);

    const handleEmailBlur = async () => {
        if (userInfo?.canEdit) {
            try {
                await updateUserInfo({ mail: email }).unwrap();
                await refetchUserInfo();
            } catch (error) {
                console.error('Failed to update email:', error);
            }
        }
    };

    const handleLanguageChange = async (newLanguage: string) => {
        setLanguage(newLanguage);
        setLocale(newLanguage as 'en' | 'de');
        try {
            await updateUserInfo({ language: newLanguage }).unwrap();
            await refetchUserInfo();
        } catch (error) {
            console.error('Failed to update language:', error);
        }
    };

    const handlePreselectChange = async (checked: boolean) => {
        setPreselect(checked);
        try {
            await updateUserInfo({ preselect: checked }).unwrap();
            await refetchUserInfo();
        } catch (error) {
            console.error('Failed to update preselect:', error);
        }
    };

    const handleAutoExpandEventsChange = async (checked: boolean) => {
        setAutoExpandEvents(checked);
        try {
            await updateUserInfo({ autoExpandEvents: checked }).unwrap();
            await refetchUserInfo();
        } catch (error) {
            console.error('Failed to update autoExpandEvents:', error);
        }
    };

    const handleReminderChange = async (key: keyof typeof reminders, checked: boolean) => {
        setReminders({ ...reminders, [key]: checked });
        try {
            await updateUserInfo({ [key]: checked }).unwrap();
            await refetchUserInfo();
        } catch (error) {
            console.error('Failed to update reminder:', error);
        }
    };

    const handleReminderCategoryChange = async (checked: boolean) => {
        setReminderCategory(checked);
        try {
            await updateUserInfo({ reminderCategory: checked }).unwrap();
            await refetchUserInfo();
        } catch (error) {
            console.error('Failed to update reminderCategory:', error);
        }
    };

    const handleNotifyWhenAbsentChange = async (checked: boolean) => {
        setNotifyWhenAbsent(checked);
        try {
            await updateUserInfo({ notifyWhenAbsent: checked }).unwrap();
            await refetchUserInfo();
        } catch (error) {
            console.error('Failed to update notifyWhenAbsent:', error);
        }
    };

    return (
        <BaseModal
            open={open}
            onClose={onClose}
            actions={
                <Button variant="contained" onClick={onClose}>
                    {LL.common.close()}
                </Button>
            }
            minHeight={{ xs: 'auto', sm: '70vh' }}
            centeredHeader={{
                content: (
                    <>
                        <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                            {userInfo.userName}
                        </Typography>
                        {userInfo.admin && (
                            <Chip
                                icon={<PersonIcon />}
                                label={LL.userCard.administrator()}
                                sx={{
                                    bgcolor: themeConstants.backgrounds.iconBox,
                                    color: 'white',
                                    backdropFilter: themeConstants.effects.backdropBlur,
                                    fontWeight: 600,
                                }}
                            />
                        )}
                    </>
                ),
            }}
        >
            <Box sx={{ p: { xs: 0, sm: 3 }, position: 'relative' }}>
                {isUpdating && (
                    <Stack
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(0,0,0,0.3)',
                            zIndex: 1000,
                            backdropFilter: 'blur(2px)',
                            borderRadius: 2,
                        }}
                    >
                        <CircularProgress size={40} />
                    </Stack>
                )}

                {/* Last Seen Section */}
                <Card
                    sx={{
                        ...styleHelpers.infoCard(
                            themeConstants.gradients.cardPrimary,
                            themeConstants.borders.primary
                        ),
                        mb: 3,
                    }}
                >
                    <CardContent>
                        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 2 }}>
                            <ScheduleIcon color="primary" />
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                }}
                            >
                                {LL.userModal.teamActivity()}
                            </Typography>
                        </Stack>
                        <Box
                            sx={{
                                maxHeight: '350px',
                                overflowY: 'auto',
                                overflowX: 'auto',
                            }}
                        >
                            {isLastSeenLoading ? (
                                <Stack
                                    direction="row"
                                    sx={{ alignItems: 'center', justifyContent: 'center', py: 4 }}
                                >
                                    <CircularProgress size={40} />
                                </Stack>
                            ) : (
                                <Table
                                    size={isSmallScreen ? 'small' : 'medium'}
                                    stickyHeader
                                    sx={{
                                        '& .MuiTableHead-root .MuiTableCell-root': {
                                            bgcolor: 'background.paper',
                                            fontWeight: 700,
                                            borderBottom: 2,
                                            borderColor: 'divider',
                                        },
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{LL.userModal.nameHeader()}</TableCell>
                                            <TableCell>{LL.userModal.lastLoginHeader()}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {lastSeen.map((row) => (
                                            <TableRow key={row.name}>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {row.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="caption"
                                                        noWrap={true}
                                                        sx={{
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        {formatDate(row.login, {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                        })}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    </CardContent>
                </Card>
                {/* Proxy Relationships Section */}
                {showProxyCard && (
                    <Card
                        sx={{
                            ...styleHelpers.infoCard(
                                themeConstants.gradients.cardSecondary,
                                themeConstants.borders.secondary
                            ),
                            mb: 3,
                        }}
                    >
                        <CardContent>
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 2 }}>
                                <PeopleIcon color="secondary" />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                    }}
                                >
                                    {LL.userModal.proxiesTitle()}
                                </Typography>
                            </Stack>

                            {/* Others who act as proxy for me */}
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 0.5,
                                }}
                            >
                                {LL.userModal.proxiesForMe()}
                            </Typography>
                            {myProxies.length === 0 ? (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        display: 'block',
                                        mb: 1.5,
                                    }}
                                >
                                    {LL.userModal.noProxiesForMe()}
                                </Typography>
                            ) : (
                                <Stack
                                    direction="row"
                                    sx={{
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        mb: 1.5,
                                    }}
                                >
                                    {myProxies.map((p) => (
                                        <Chip
                                            key={p.proxyId}
                                            size="small"
                                            icon={<PersonIcon />}
                                            label={p.proxyName}
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Stack>
                            )}

                            {/* People I act as proxy for */}
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 0.5,
                                }}
                            >
                                {LL.userModal.iAmProxyFor()}
                            </Typography>
                            {iAmProxyFor.length === 0 ? (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        display: 'block',
                                    }}
                                >
                                    {LL.userModal.notProxyForAnyone()}
                                </Typography>
                            ) : (
                                <Stack
                                    direction="row"
                                    sx={{
                                        flexWrap: 'wrap',
                                        gap: 1,
                                    }}
                                >
                                    {iAmProxyFor.map((p) => (
                                        <Chip
                                            key={p.userId}
                                            size="small"
                                            icon={<PersonIcon />}
                                            label={p.userName}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Preferences Section */}
                <Card
                    sx={{
                        ...styleHelpers.infoCard(
                            themeConstants.gradients.cardPrimary,
                            themeConstants.borders.primary
                        ),
                        mb: 3,
                    }}
                >
                    <CardContent>
                        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 2 }}>
                            <CheckCircleIcon color="secondary" />
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                }}
                            >
                                {LL.userModal.preferences()}
                            </Typography>
                        </Stack>

                        <Stack spacing={2}>
                            <FormControl fullWidth size={isSmallScreen ? 'medium' : 'small'}>
                                <InputLabel>{LL.common.language()}</InputLabel>
                                <Select
                                    value={language}
                                    label={LL.common.language()}
                                    onChange={(e) => handleLanguageChange(e.target.value)}
                                    sx={{
                                        borderRadius: 2,
                                    }}
                                >
                                    <MenuItem value="en">{LL.common.languageEnglish()}</MenuItem>
                                    <MenuItem value="de">{LL.common.languageGerman()}</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={preselect}
                                        onChange={(e) => handlePreselectChange(e.target.checked)}
                                        size={isSmallScreen ? 'medium' : 'small'}
                                        sx={{
                                            color: 'secondary.main',
                                            '&.Mui-checked': {
                                                color: 'secondary.main',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                            }}
                                        >
                                            {LL.userModal.autoSelectName()}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'text.secondary',
                                            }}
                                        >
                                            {LL.userModal.autoSelectDescription()}
                                        </Typography>
                                    </Box>
                                }
                                sx={styleHelpers.settingsOption(
                                    'rgba(139,92,246,0.1)',
                                    'secondary.main'
                                )}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={autoExpandEvents}
                                        onChange={(e) =>
                                            handleAutoExpandEventsChange(e.target.checked)
                                        }
                                        size={isSmallScreen ? 'medium' : 'small'}
                                        sx={{
                                            color: 'secondary.main',
                                            '&.Mui-checked': {
                                                color: 'secondary.main',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                            }}
                                        >
                                            {LL.userModal.autoExpandEventsName()}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'text.secondary',
                                            }}
                                        >
                                            {LL.userModal.autoExpandEventsDescription()}
                                        </Typography>
                                    </Box>
                                }
                                sx={styleHelpers.settingsOption(
                                    'rgba(139,92,246,0.1)',
                                    'secondary.main'
                                )}
                            />
                        </Stack>
                    </CardContent>
                </Card>

                {/* Email Section */}
                <Card
                    sx={{
                        ...styleHelpers.infoCard(
                            themeConstants.gradients.cardSuccess,
                            themeConstants.borders.success
                        ),
                        mb: 3,
                    }}
                >
                    <CardContent>
                        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 2 }}>
                            <EmailIcon color="success" />
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                }}
                            >
                                {LL.userModal.emailSettings()}
                            </Typography>
                        </Stack>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label={LL.userModal.yourEmail()}
                                type="email"
                                value={email}
                                disabled={!userInfo?.canEdit}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={handleEmailBlur}
                                slotProps={{ htmlInput: { maxLength: 60 } }}
                                size={isSmallScreen ? 'medium' : 'small'}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />

                            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                                <NotificationsIcon color="warning" />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                    }}
                                >
                                    {LL.userModal.emailNotifications()}
                                </Typography>
                            </Stack>
                            <Grid container spacing={1.5}>
                                {[
                                    {
                                        key: 'reminder7d' as const,
                                        label: LL.common.days({ count: 7 }),
                                        color: '#3b82f6',
                                    },
                                    {
                                        key: 'reminder3d' as const,
                                        label: LL.common.days({ count: 3 }),
                                        color: '#8b5cf6',
                                    },
                                    {
                                        key: 'reminder2d' as const,
                                        label: LL.common.days({ count: 2 }),
                                        color: '#f59e0b',
                                    },
                                    {
                                        key: 'reminder1d' as const,
                                        label: LL.common.days({ count: 1 }),
                                        color: '#ef4444',
                                    },
                                ].map(({ key, label, color }) => (
                                    <Grid size={{ xs: 6, sm: 3 }} key={key}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={reminders[key]}
                                                    onChange={(e: any) =>
                                                        handleReminderChange(key, e.target.checked)
                                                    }
                                                    size={isSmallScreen ? 'medium' : 'small'}
                                                    sx={{
                                                        color: color,
                                                        '&.Mui-checked': {
                                                            color: color,
                                                        },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                            }
                                            sx={{
                                                ...styleHelpers.settingsOption(
                                                    'rgba(245,158,11,0.1)',
                                                    'warning.main'
                                                ),
                                            }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={notifyWhenAbsent}
                                        onChange={(e) =>
                                            handleNotifyWhenAbsentChange(e.target.checked)
                                        }
                                        size={isSmallScreen ? 'medium' : 'small'}
                                        sx={{
                                            color: 'primary.main',
                                            '&.Mui-checked': { color: 'primary.main' },
                                        }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                            }}
                                        >
                                            {LL.userModal.notifyWhenAbsentName()}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'text.secondary',
                                            }}
                                        >
                                            {LL.userModal.notifyWhenAbsentDescription()}
                                        </Typography>
                                    </Box>
                                }
                                sx={styleHelpers.settingsOption(
                                    'rgba(59,130,246,0.06)',
                                    'primary.main'
                                )}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={reminderCategory}
                                        onChange={(e) =>
                                            handleReminderCategoryChange(e.target.checked)
                                        }
                                        size={isSmallScreen ? 'medium' : 'small'}
                                        sx={{
                                            color: 'primary.main',
                                            '&.Mui-checked': { color: 'primary.main' },
                                        }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                            }}
                                        >
                                            {LL.userModal.reminderCategoryName
                                                ? LL.userModal.reminderCategoryName()
                                                : 'Category reminders'}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'text.secondary',
                                            }}
                                        >
                                            {LL.userModal.reminderCategoryDescription
                                                ? LL.userModal.reminderCategoryDescription()
                                                : 'Receive reminders when roles you can take are missing assignments.'}
                                        </Typography>
                                    </Box>
                                }
                                sx={styleHelpers.settingsOption(
                                    'rgba(59,130,246,0.06)',
                                    'primary.main'
                                )}
                            />
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        </BaseModal>
    );
};

export default UserModal;
